import { useImperativeHandle, useRef, useState } from 'react';
import type { FormEvent, Ref } from 'react';
import { useTranslation } from 'react-i18next';

import { NgButton, NgModal } from '@/ui';
import type { NgModalHandle } from '@/ui';
import { useEditForm } from '@/ui/forms/useEditForm';
import { usePsapConfiguration } from '@/app/shell/psapConfiguration';
import { PhoneNumber } from '@/lib/phone/phoneNumber';
import { updateCallerPhoneNumber } from '../actions/emsCommands';
import { validateAddPhoneNumber } from './addPhoneNumberModel';

const validatePhoneNumberField = (value: string): string[] => {
  const message = validateAddPhoneNumber(value);
  return message === null ? [] : [message];
};

const FORM_ID = `form-${crypto.randomUUID()}`;
const VALUE_INPUT_ID = 'phone-number';

export type AddPhoneNumberDialogHandle = {
  showModal: () => void;
};

export type AddPhoneNumberDialogProps = {
  incidentId: string;
  ref?: Ref<AddPhoneNumberDialogHandle>;
};

export function AddPhoneNumberDialog({ incidentId, ref }: AddPhoneNumberDialogProps) {
  const { t } = useTranslation();
  const psapConfiguration = usePsapConfiguration();
  const defaultCountryCode = (psapConfiguration.psapPortalConfiguration.defaultCountryCode as string | null) ?? '';

  const modalRef = useRef<NgModalHandle>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [phoneNumber, setPhoneNumber] = useState('');
  const editForm = useEditForm<'phoneNumber'>({
    fieldValidators: { phoneNumber: validatePhoneNumberField },
  });

  useImperativeHandle(ref, () => ({
    showModal: () => {
      modalRef.current?.show();
      setPhoneNumber(defaultCountryCode);
      editForm.reset({ phoneNumber: defaultCountryCode });
    },
  }));

  const closeDialog = () => {
    modalRef.current?.hide();
  };

  const formSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!editForm.validate(() => ({ phoneNumber: validatePhoneNumberField(phoneNumber) }))) {
      return;
    }

    await updateCallerPhoneNumber(incidentId, new PhoneNumber(phoneNumber).value);
    closeDialog();
  };

  const focusInput = () => {
    inputRef.current?.focus();
  };

  return (
    <NgModal
      ref={modalRef}
      header={t('AddPhoneNumberDialog_Header')}
      onShown={focusInput}
      bodyTemplate={
        <form id={FORM_ID} onSubmit={(event) => void formSubmit(event)}>
          <div style={{ minHeight: '7rem' }}>
            <label htmlFor={VALUE_INPUT_ID} className="label">
              {t('NewIncidentDialog_Label_PhoneNumber')}
            </label>

            <input
              ref={inputRef}
              id={VALUE_INPUT_ID}
              className={editForm.fieldCssClass('phoneNumber', 'form-control')}
              value={phoneNumber}
              onChange={(event) => {
                setPhoneNumber(event.target.value);
                editForm.notifyFieldInput('phoneNumber', event.target.value);
              }}
              onBlur={() => editForm.notifyFieldBlur('phoneNumber')}
            />
            {editForm.validationMessages('phoneNumber').map((message, index) => (
              <div key={index} className="validation-message">
                {message}
              </div>
            ))}
          </div>
        </form>
      }
      footerTemplate={
        <>
          <NgButton submitButton form={FORM_ID} icon="fal fa-check-circle" text={t('Button_Save')} />
          <NgButton text={t('Button_Cancel')} outline onClick={closeDialog} />
        </>
      }
    />
  );
}
